// 대시보드 「오늘 할 일」 — 응대 안 한 문의 · 시공중 현장별 오늘 일지 여부(크론 log_reminder 와 같은 기준). 「오늘」은 KST 날짜 문자열로 받는다.

import type { Customer } from "../customer/types";
import { isProjectWorkday } from "../project/cron-select";
import type { Project } from "../project/types";
import { dDayLabel, dDayOf } from "../project/view";

type BoardProject = Pick<
  Project,
  | "id"
  | "title"
  | "status"
  | "planned_end_date"
  | "work_saturday"
  | "work_sunday"
  | "work_holiday"
  | "schedule_holidays"
>;

export interface SiteLine {
  id: string;
  title: string;
  // 준공 예정일 기준 D-day(「D-3」·「D+2」) — 날짜가 없으면 빈 문자열.
  end_d_day: string;
  logged_today: boolean;
  // 오늘이 이 현장의 작업일인지 — 쉬는 날이면 기록이 없어도 챙길 일이 아니다.
  workday: boolean;
}

export interface TodayBoard {
  // 고객 단계 「신규」 — 아직 연락하지 않은 문의.
  new_inquiries: number;
  in_progress: number;
  // 시공중 · 오늘이 작업일 · 오늘 현장일지가 없는 현장(사진만 있으면 없는 것).
  not_logged: number;
  sites: SiteLine[];
}

// 챙길 현장 — 작업일인데 오늘 일지가 없다.
export const isMissing = (site: Pick<SiteLine, "logged_today" | "workday">): boolean =>
  site.workday && !site.logged_today;

export const buildTodayBoard = (
  customers: readonly Pick<Customer, "stage">[],
  projects: readonly BoardProject[],
  loggedIds: ReadonlySet<string>,
  today: string
): TodayBoard => {
  const sites = projects
    .filter((p) => p.status === "IN_PROGRESS")
    .map(
      (p): SiteLine => ({
        id: p.id,
        title: p.title,
        end_d_day: dDayLabel(dDayOf(p.planned_end_date, today)),
        logged_today: loggedIds.has(p.id),
        workday: isProjectWorkday(p, today),
      })
    )
    .sort(
      (a, b) =>
        Number(isMissing(b)) - Number(isMissing(a)) || a.title.localeCompare(b.title, "ko")
    );
  return {
    new_inquiries: customers.filter((c) => c.stage === "NEW").length,
    in_progress: sites.length,
    not_logged: sites.filter(isMissing).length,
    sites,
  };
};
